import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const premiumButtonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Primary - Ultramarine Purple Gradient
        primary: "bg-gradient-to-r from-primary via-primary/90 to-primary/80 text-primary-foreground shadow-lg hover:shadow-xl hover:shadow-primary/25 hover:scale-[1.02] active:scale-[0.98] border border-primary/20",
        
        // Accent - Electric Cherry Power
        accent: "bg-gradient-to-r from-accent via-accent/90 to-accent/80 text-accent-foreground shadow-lg hover:shadow-xl hover:shadow-accent/25 hover:scale-[1.02] active:scale-[0.98] border border-accent/20",
        
        // Premium - Sophisticated with glow
        premium: "bg-gradient-to-r from-neutral-900 via-neutral-800 to-neutral-900 text-white shadow-premium hover:shadow-lg hover:bg-gradient-to-r hover:from-neutral-800 hover:via-neutral-700 hover:to-neutral-800 border border-neutral-700/50 hover:border-neutral-600/50",
        
        // Glass morphism
        glass: "glass text-foreground hover:bg-white/90 border hover:border-primary/20 hover:shadow-md",
        
        // Outline with premium styling
        outline: "border-2 border-border bg-transparent hover:bg-accent/5 hover:border-accent/50 hover:text-accent transition-colors",
        
        // Secondary premium
        secondary: "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 border border-border hover:shadow-md",
        
        // Ghost with hover glow
        ghost: "hover:bg-accent/10 hover:text-accent hover:shadow-md transition-all duration-200",
        
        // Link with gradient
        link: "text-primary underline-offset-4 hover:underline hover:text-accent transition-colors",
        
        // Success variant
        success: "bg-gradient-to-r from-success via-success/90 to-success/80 text-success-foreground shadow-lg hover:shadow-xl hover:shadow-success/25 hover:scale-[1.02] active:scale-[0.98]",
        
        // Warning variant
        warning: "bg-gradient-to-r from-warning via-warning/90 to-warning/80 text-warning-foreground shadow-lg hover:shadow-xl hover:shadow-warning/25 hover:scale-[1.02] active:scale-[0.98]",
        
        // Destructive - uses Electric Cherry
        destructive: "bg-gradient-to-r from-destructive via-destructive/90 to-destructive/80 text-destructive-foreground shadow-lg hover:shadow-xl hover:shadow-destructive/25 hover:scale-[1.02] active:scale-[0.98]",
      },
      size: {
        xs: "h-7 rounded-md px-2 text-xs",
        sm: "h-8 rounded-md px-3 text-xs",
        default: "h-10 px-4 py-2",
        lg: "h-12 rounded-lg px-6 text-base",
        xl: "h-14 rounded-xl px-8 text-lg font-semibold",
        icon: "h-10 w-10",
      },
      glow: {
        none: "",
        subtle: "hover:shadow-lg",
        medium: "hover:shadow-xl hover:shadow-current/20",
        strong: "shadow-lg shadow-current/20 hover:shadow-2xl hover:shadow-current/30",
      }
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
      glow: "none",
    },
  }
)

export interface PremiumButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof premiumButtonVariants> {
  asChild?: boolean
}

const PremiumButton = React.forwardRef<HTMLButtonElement, PremiumButtonProps>(
  ({ className, variant, size, glow, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(premiumButtonVariants({ variant, size, glow, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
PremiumButton.displayName = "PremiumButton"

export { PremiumButton, premiumButtonVariants } 